import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Button,
  Input,
  Textarea,
  Badge,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Checkbox,
} from '@h2/ui';
import { X, Plus } from 'lucide-react';
import { useWorkerGroupStore, WorkerGroup } from '../../stores/useWorkerGroupStore';
import { useWorkerStore } from '../../stores/useWorkerStore';

const groupSchema = z.object({
  name: z.string().min(1, '그룹명을 입력해주세요').max(50, '그룹명은 50자 이내로 입력해주세요'),
  description: z.string().min(1, '그룹 설명을 입력해주세요').max(200, '설명은 200자 이내로 입력해주세요'),
  status: z.enum(['active', 'inactive']),
  teamLead: z.string().optional(),
});

type GroupFormData = z.infer<typeof groupSchema>;

interface WorkerGroupFormModalProps {
  group?: WorkerGroup | null;
  onClose: () => void;
}

export const WorkerGroupFormModal: React.FC<WorkerGroupFormModalProps> = ({ group, onClose }) => {
  const { createGroup, updateGroup } = useWorkerGroupStore();
  const { workers } = useWorkerStore();
  
  const [specialties, setSpecialties] = useState<string[]>(group?.specialties || []);
  const [newSpecialty, setNewSpecialty] = useState('');
  const [selectedMembers, setSelectedMembers] = useState<string[]>(group?.members || []);
  
  const isEditing = !!group;

  const form = useForm<GroupFormData>({
    resolver: zodResolver(groupSchema),
    defaultValues: {
      name: group?.name || '',
      description: group?.description || '',
      status: group?.status || 'active',
      teamLead: group?.teamLead || '',
    },
  });

  const onSubmit = async (data: GroupFormData) => {
    try {
      const groupData = {
        ...data,
        specialties: specialties,
        members: selectedMembers,
      };

      if (isEditing && group) {
        await updateGroup(group.id, groupData);
      } else {
        await createGroup(groupData);
      }

      onClose();
    } catch (error) {
      console.error('그룹 저장 실패:', error);
    }
  };

  const addSpecialty = () => {
    if (newSpecialty.trim() && !specialties.includes(newSpecialty.trim())) {
      setSpecialties([...specialties, newSpecialty.trim()]);
      setNewSpecialty('');
    }
  };

  const removeSpecialty = (specialtyToRemove: string) => {
    setSpecialties(specialties.filter(specialty => specialty !== specialtyToRemove));
  };

  const handleSpecialtyKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addSpecialty();
    }
  };

  const toggleMember = (workerId: string) => {
    setSelectedMembers(prev =>
      prev.includes(workerId)
        ? prev.filter(id => id !== workerId)
        : [...prev, workerId]
    );
  };

  const activeWorkers = workers.filter(worker => worker.status === 'active');

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? '그룹 수정' : '새 그룹 등록'}</DialogTitle>
          <DialogDescription>
            작업자 그룹의 기본 정보와 멤버를 설정하세요.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              {/* 그룹명 */}
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>그룹명</FormLabel>
                    <FormControl>
                      <Input placeholder="한영 번역팀" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {/* 상태 */}
              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>상태</FormLabel>
                    <FormControl>
                      <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                        <option value="active">활성</option>
                        <option value="inactive">비활성</option>
                      </select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* 그룹 설명 */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>그룹 설명</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="한국어-영어 전문 번역 그룹입니다."
                      className="resize-none"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 전문 분야 */}
            <div className="space-y-3">
              <FormLabel>전문 분야</FormLabel>
              <div className="flex gap-2">
                <Input
                  placeholder="전문 분야 추가 (예: 한국어, 영어, 번역)"
                  value={newSpecialty}
                  onChange={(e) => setNewSpecialty(e.target.value)}
                  onKeyDown={handleSpecialtyKeyDown}
                  className="flex-1"
                />
                <Button
                  type="button"
                  onClick={addSpecialty}
                  disabled={!newSpecialty.trim()}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              
              {specialties.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                  {specialties.map((specialty) => (
                    <Badge key={specialty} variant="secondary" className="text-sm">
                      {specialty}
                      <button
                        type="button"
                        onClick={() => removeSpecialty(specialty)}
                        className="ml-1 hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
            </div>

            {/* 팀 리더 */}
            <FormField
              control={form.control}
              name="teamLead"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>팀 리더</FormLabel>
                  <FormControl>
                    <select {...field} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background">
                      <option value="">팀 리더 선택</option>
                      {activeWorkers.map((worker) => (
                        <option key={worker.id} value={worker.id}>
                          {worker.name} ({worker.email})
                        </option>
                      ))}
                    </select>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* 멤버 선택 */}
            <div className="space-y-3">
              <FormLabel>그룹 멤버</FormLabel>
              <div className="grid grid-cols-2 gap-3 max-h-60 overflow-y-auto border rounded-md p-3">
                {activeWorkers.length === 0 ? (
                  <div className="col-span-2 text-center py-4 text-muted-foreground">
                    등록된 활성 작업자가 없습니다.
                  </div>
                ) : (
                  activeWorkers.map((worker) => (
                    <div key={worker.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={worker.id}
                        checked={selectedMembers.includes(worker.id)}
                        onCheckedChange={() => toggleMember(worker.id)}
                      />
                      <label
                        htmlFor={worker.id}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        <div className="flex items-center space-x-2">
                          <span>{worker.avatar || '👤'}</span>
                          <div>
                            <div>{worker.name}</div>
                            <div className="text-xs text-muted-foreground">
                              {worker.skills.slice(0, 2).join(', ')}
                              {worker.skills.length > 2 && '...'}
                            </div>
                          </div>
                        </div>
                      </label>
                    </div>
                  ))
                )}
              </div>
              
              {selectedMembers.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  선택된 멤버: {selectedMembers.length}명
                </div>
              )}
            </div>

            {/* 버튼 */}
            <div className="flex justify-end space-x-3">
              <Button type="button" variant="outline" onClick={onClose}>
                취소
              </Button>
              <Button type="submit">
                {isEditing ? '수정' : '등록'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
